import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import { findAndReplace } from 'mdast-util-find-and-replace';
// ──────────────────────────────────────────────────────────────────────────────
// 1) WIKILINK PLUGIN
//    Синтаксис: [[slug]] | [[slug|підпис]] | [[slug\|підпис]] (escaped pipe)
//    Анкори # поки не підтримуємо — у живому контенті їх немає.
// ──────────────────────────────────────────────────────────────────────────────
// Дозволяємо в slug літери, цифри, дефіс, підкреслення, крапку.
// Slug: [^|\]] (не pipe і не closing bracket).
// Підпис після pipe — будь-що крім ]].
const WIKILINK_REGEX = /\[\[([^|\]\n]+?)(?:\\?\|([^\]\n]+?))?\]\]/g;
function remarkWikilinks(opts) {
    const known = opts.knownSlugs;
    const base = opts.wikiBase ?? '/wiki/';
    return () => (tree) => {
        findAndReplace(tree, [
            [
                WIKILINK_REGEX,
                (_match, slugRaw, labelRaw) => {
                    const slug = slugRaw.trim();
                    const label = (labelRaw ?? slug).trim();
                    const isDead = known ? !known.has(slug) : false;
                    const node = {
                        type: 'link',
                        url: `${base}${encodeURIComponent(slug)}`,
                        title: isDead ? `Битe посилання: ${slug}` : slug,
                        data: {
                            hProperties: {
                                className: isDead ? ['wlink', 'wdead'] : ['wlink'],
                                'data-slug': slug,
                                'data-dead': isDead ? 'true' : null,
                            },
                        },
                        children: [{ type: 'text', value: label }],
                    };
                    return node;
                },
            ],
        ]);
    };
}
// ──────────────────────────────────────────────────────────────────────────────
// 2) DIRECTIVES PLUGIN
//    Тільки два live-патерни:
//      :::hero  → <section class="hero"> з h1/h2/тіло
//      :::cards → <div class="cards-block"> з карток, кожна — "wlink | chip | desc"
// ──────────────────────────────────────────────────────────────────────────────
function remarkDirectiveHandler() {
    return () => (tree) => {
        visit(tree, (node) => {
            if (!isDirective(node))
                return;
            if (node.name === 'hero') {
                const data = node.data ?? (node.data = {});
                data.hName = 'section';
                data.hProperties = { className: ['hero'] };
                return;
            }
            if (node.name === 'cards') {
                const cards = parseCardsBlock(node.children);
                node.children = cards;
                const data = node.data ?? (node.data = {});
                data.hName = 'div';
                data.hProperties = { className: ['cards-block'] };
                return;
            }
            // Невідома директива — деградуємо до простого div зі своїм class.
            if (isDirective(node)) {
                const data = node.data ?? (node.data = {});
                data.hName = 'div';
                data.hProperties = { className: ['directive', `directive-${node.name}`] };
            }
        });
    };
}
function isDirective(n) {
    if (typeof n !== 'object' || n === null)
        return false;
    const t = n.type;
    return t === 'containerDirective' || t === 'leafDirective' || t === 'textDirective';
}
// Простий visitor, без зовнішніх залежностей (`unist-util-visit` шумна для цього).
function visit(node, fn) {
    fn(node);
    if (typeof node !== 'object' || node === null)
        return;
    const children = node.children;
    if (Array.isArray(children)) {
        for (const child of children)
            visit(child, fn);
    }
}
/**
 * Парсить тіло `:::cards` блоку.
 * Очікуваний формат — лісь маркер-список, кожен рядок:
 *   `- [[slug|підпис]] | Чип | Опис`
 * де частини розділені вертикальною рискою (НЕ всередині [[ ]]).
 *
 * Повертає масив mdast-нод <div class="card-tile">…</div> через hChildren.
 */
function parseCardsBlock(children) {
    const out = [];
    // Шукаємо list-нод верхнього рівня
    for (const child of children) {
        if (child.type !== 'list') {
            // Все інше залишаємо як інтро/заголовок над картками
            out.push(child);
            continue;
        }
        for (const li of child.children) {
            if (li.type !== 'listItem')
                continue;
            // Дістаємо лінійний текст першого параграфа елемента списку
            const first = li.children[0];
            if (!first || first.type !== 'paragraph')
                continue;
            const parts = splitOnTopLevelPipe(first.children);
            const [linkPart, chipPart, descPart, ...rest] = parts;
            const cardChildren = [];
            if (linkPart && linkPart.length) {
                cardChildren.push({
                    type: 'paragraph',
                    children: linkPart,
                    data: { hName: 'h4', hProperties: { className: ['card-title'] } },
                });
            }
            if (chipPart && chipPart.length) {
                cardChildren.push({
                    type: 'paragraph',
                    children: chipPart,
                    data: { hName: 'span', hProperties: { className: ['card-chip', 'chip'] } },
                });
            }
            const tailParts = descPart ? [descPart, ...rest] : [];
            if (tailParts.length) {
                const descChildren = tailParts.flatMap((p, i) => i === 0 ? p : [{ type: 'text', value: ' · ' }, ...p]);
                cardChildren.push({
                    type: 'paragraph',
                    children: descChildren,
                    data: { hName: 'p', hProperties: { className: ['card-desc'] } },
                });
            }
            out.push({
                type: 'paragraph',
                children: cardChildren,
                data: { hName: 'article', hProperties: { className: ['card-tile'] } },
            });
        }
    }
    return out;
}
/**
 * Розбиває inline-діти за вертикальною рискою на верхньому рівні.
 * Не ріже всередині тексту, що йде підряд як один TextNode і має ' | ' —
 * ріжемо саме по " | " (з пробілами навколо) щоб не зачепити piped wikilinks
 * (бо плагін wikilinks вже перетворив їх на Link-ноди).
 */
function splitOnTopLevelPipe(nodes) {
    const groups = [[]];
    for (const n of nodes) {
        if (n.type === 'text' && typeof n.value === 'string') {
            const text = n.value;
            const segs = text.split(/\s+\|\s+/);
            if (segs.length === 1) {
                groups[groups.length - 1].push(n);
                continue;
            }
            const firstSeg = segs[0] ?? '';
            if (firstSeg) {
                groups[groups.length - 1].push({ type: 'text', value: firstSeg });
            }
            for (let i = 1; i < segs.length; i++) {
                groups.push([]);
                const s = segs[i] ?? '';
                if (s) {
                    groups[groups.length - 1].push({ type: 'text', value: s });
                }
            }
        }
        else {
            groups[groups.length - 1].push(n);
        }
    }
    return groups.map((g) => trimInline(g));
}
function trimInline(nodes) {
    const arr = nodes.slice();
    while (arr.length && arr[0].type === 'text') {
        const v = arr[0].value.replace(/^\s+/, '');
        if (!v) {
            arr.shift();
            continue;
        }
        arr[0].value = v;
        break;
    }
    while (arr.length && arr[arr.length - 1].type === 'text') {
        const last = arr[arr.length - 1];
        const v = last.value.replace(/\s+$/, '');
        if (!v) {
            arr.pop();
            continue;
        }
        last.value = v;
        break;
    }
    return arr;
}
// ──────────────────────────────────────────────────────────────────────────────
// 3) STAT-BLOCK TABLE STYLING
//    Помічаємо двоколонкові markdown-таблиці (Параметр | Значення) і додаємо
//    клас .statblock-table до <table>, щоб CSS міг їх стилізувати.
// ──────────────────────────────────────────────────────────────────────────────
function rehypeStatblockTables() {
    return () => (tree) => {
        visit(tree, (node) => {
            const n = node;
            if (!n || n.type !== 'element' || n.tagName !== 'table')
                return;
            // Перевіряємо: 2 колонки, перший заголовок «Параметр»
            const thead = n.children.find((c) => c.type === 'element' && c.tagName === 'thead');
            if (!thead)
                return;
            const headerRow = thead.children.find((c) => c.type === 'element' && c.tagName === 'tr');
            if (!headerRow)
                return;
            const cells = headerRow.children.filter((c) => c.type === 'element' && c.tagName === 'th');
            if (cells.length !== 2)
                return;
            const first = extractText(cells[0]).trim().toLowerCase();
            if (first !== 'параметр')
                return;
            const props = (n.properties ??= {});
            const cls = props.className ?? [];
            props.className = [...cls, 'statblock-table'];
        });
    };
}
function extractText(node) {
    if (!node || !node.children)
        return '';
    let out = '';
    for (const c of node.children) {
        if (c.type === 'text') {
            out += c.value ?? '';
        }
        else if (c.type === 'element') {
            out += extractText(c);
        }
    }
    return out;
}
// ──────────────────────────────────────────────────────────────────────────────
// 4) PUBLIC API
// ──────────────────────────────────────────────────────────────────────────────
/**
 * Розширена sanitize-схема. Дозволяємо:
 *   - усі стандартні безпечні теги з defaultSchema + 'section'/'article'/'mark'
 *   - className на всіх елементах (для наших класів wlink/cards-block/hero тощо)
 *   - data-slug/data-dead на <a> (для wikilink-метаданих)
 *
 * Блокуємо: <script>, <iframe>, on*=, javascript:/data: (image data: OK).
 */
const sanitizeSchema = {
    ...defaultSchema,
    attributes: {
        ...defaultSchema.attributes,
        '*': [...(defaultSchema.attributes?.['*'] ?? []), 'className', 'dataSlug', 'dataDead'],
    },
    tagNames: [...(defaultSchema.tagNames ?? []), 'section', 'article', 'mark'],
};
/**
 * Перетворює markdown тіло wiki-сторінки в HTML-рядок.
 * Sanitize-крок блокує <script>, on*=, javascript: URLs тощо.
 */
export function renderMarkdown(md, options = {}) {
    const processor = unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkDirective)
        .use(remarkWikilinks(options))
        .use(remarkDirectiveHandler())
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(rehypeRaw)
        .use(rehypeStatblockTables())
        // КРИТИЧНО: запобігає GM-XSS у тілі wiki. Без цього хтось пише
        // <script>fetch('/x?s='+localStorage['sd:gm-secret'])</script> в page,
        // і при перегляді себе ж витягує секрет.
        .use(rehypeSanitize, sanitizeSchema)
        .use(rehypeStringify);
    const result = processor.processSync(md);
    return String(result);
}
/**
 * Знаходить усі унікальні slug-и, на які посилається тіло сторінки.
 * Корисно для backlink-індексу.
 */
export function extractWikilinks(md) {
    const seen = new Set();
    for (const m of md.matchAll(WIKILINK_REGEX)) {
        const slug = (m[1] ?? '').trim();
        if (slug)
            seen.add(slug);
    }
    return [...seen];
}
//# sourceMappingURL=markdown.js.map